'use client'

import {
  MicrophoneIcon,
  StopIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'

interface VoiceInputOverlayProps {
  isRecording: boolean
  isConnecting: boolean
  isDisabled: boolean
  onStartRecording: () => void
  onStopRecording: () => void
}

/**
 * Voice input button overlay for the main textarea.
 * Shows connecting state with spinner, recording/streaming state with pulse animation.
 */
export function VoiceInputOverlay({
  isRecording,
  isConnecting,
  isDisabled,
  onStartRecording,
  onStopRecording,
}: VoiceInputOverlayProps) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-2 flex items-center justify-end px-1">
      <div className="pointer-events-auto">
        <button
          type="button"
          onClick={isRecording ? onStopRecording : onStartRecording}
          disabled={isDisabled || isConnecting}
          className={cn(
            'relative flex items-center justify-center gap-1.5 rounded-md transition-all',
            'h-8 w-8',
            isRecording
              ? 'bg-destructive/10 text-destructive'
              : 'text-muted-foreground hover:bg-accent/10 hover:text-foreground',
            (isDisabled || isConnecting) && 'cursor-not-allowed opacity-40',
          )}
          title={
            isConnecting
              ? 'Connecting...'
              : isRecording
                ? 'Stop recording'
                : 'Voice input'
          }
          aria-label={
            isConnecting
              ? 'Connecting to voice service'
              : isRecording
                ? 'Stop recording'
                : 'Start voice input'
          }
        >
          {isConnecting ? (
            <CircleNotchIcon className="h-4 w-4 animate-spin" />
          ) : isRecording ? (
            <>
              <StopIcon className="h-4 w-4" weight="fill" />
              <span className="bg-destructive/20 absolute inset-0 animate-ping rounded-full" />
            </>
          ) : (
            <MicrophoneIcon className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  )
}
